/**
 * English: Device-scoped memory summaries (events rollups + filtered station summaries).
 * Thai: สรุปหน่วยความจำต่ออุปกรณ์ (rollup จาก events + กรองสรุประดับสถานี)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ENDPOINTS } from '../config/api';
import {
  filterSummariesForDevice,
  mergeDailySummaries,
  mergePeriodicSummaries,
  rollupEventsToDaily,
  rollupEventsToPeriodic,
  type MemoryEventRow,
} from '../components/agent-management/memoryDeviceRollups';
import type {
  DailySummaryResponse,
  LatestSummaryResponse,
  PeriodicSummaryResponse,
} from './useMemorySummary';
import { useMemorySummaryDaily, useMemorySummaryLatest, useMemorySummaryPeriodic } from './useMemorySummary';
import { useApi } from './useApi';

type HookState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

function emptyLatest(orgId: string, stationId: string): LatestSummaryResponse {
  return { daily: null, periodic_latest: {}, org_id: orgId, station_id: stationId };
}

export function useDeviceMemory(
  orgId: string,
  stationId: string,
  deviceId: string,
  agentId?: string,
): {
  latest: HookState<LatestSummaryResponse>;
  daily: HookState<DailySummaryResponse>;
  periodic: HookState<PeriodicSummaryResponse>;
} {
  const { get } = useApi();
  const stationLatest = useMemorySummaryLatest(orgId, stationId);
  const stationDaily = useMemorySummaryDaily(orgId, stationId, 30, 0, 30);
  const stationPeriodic = useMemorySummaryPeriodic('', orgId, stationId, 0, 30);

  const [events, setEvents] = useState<MemoryEventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const refreshEvents = useCallback(async () => {
    const o = orgId.trim();
    const s = stationId.trim();
    const d = deviceId.trim();
    if (!o || !s || !d) {
      setEvents([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    try {
      const path = `${ENDPOINTS.memoryDeviceEvents(d)}?org_id=${encodeURIComponent(o)}&station_id=${encodeURIComponent(s)}&limit=200`;
      const json = (await get(path)) as { items?: MemoryEventRow[] };
      setEvents(Array.isArray(json.items) ? json.items : []);
      setEventsError(null);
    } catch (e) {
      setEvents([]);
      setEventsError(e instanceof Error ? e.message : 'Failed to fetch device memory events');
    } finally {
      setEventsLoading(false);
    }
  }, [get, orgId, stationId, deviceId]);

  useEffect(() => {
    void refreshEvents();
  }, [refreshEvents]);

  const eventDaily = useMemo(
    () => rollupEventsToDaily(events, orgId, stationId),
    [events, orgId, stationId],
  );
  const eventPeriodic = useMemo(
    () => rollupEventsToPeriodic(eventDaily, orgId, stationId),
    [eventDaily, orgId, stationId],
  );

  const filteredStationDaily = useMemo(
    () => filterSummariesForDevice(stationDaily.data?.items ?? [], deviceId, agentId),
    [stationDaily.data, deviceId, agentId],
  );
  const filteredStationPeriodic = useMemo(
    () => filterSummariesForDevice(stationPeriodic.data?.items ?? [], deviceId, agentId),
    [stationPeriodic.data, deviceId, agentId],
  );

  const mergedDailyItems = useMemo(
    () => mergeDailySummaries(eventDaily, filteredStationDaily),
    [eventDaily, filteredStationDaily],
  );
  const mergedPeriodicItems = useMemo(
    () => mergePeriodicSummaries(eventPeriodic, filteredStationPeriodic),
    [eventPeriodic, filteredStationPeriodic],
  );

  const latestData = useMemo((): LatestSummaryResponse => {
    const base = stationLatest.data ?? emptyLatest(orgId, stationId);
    const dailyFromMerged =
      mergedDailyItems.length > 0
        ? [...mergedDailyItems].sort((a, b) => b.summary_date.localeCompare(a.summary_date))[0]
        : null;
    const dailyFiltered =
      base.daily && filterSummariesForDevice([base.daily], deviceId, agentId).length > 0
        ? base.daily
        : null;
    const daily = dailyFromMerged ?? dailyFiltered;

    const periodic_latest: LatestSummaryResponse['periodic_latest'] = {};
    for (const key of ['week', 'month', 'quarter', 'year'] as const) {
      const fromStation = base.periodic_latest?.[key];
      if (fromStation && memoryMentionsDeviceSafe(fromStation.summary_md, deviceId, agentId)) {
        periodic_latest[key] = fromStation;
      }
    }
    const latestPeriodic = [...mergedPeriodicItems].sort((a, b) =>
      b.period_start_date.localeCompare(a.period_start_date),
    )[0];
    if (latestPeriodic?.period_type === 'week') {
      periodic_latest.week = latestPeriodic;
    }

    return { ...base, daily, periodic_latest, org_id: orgId, station_id: stationId };
  }, [stationLatest.data, mergedDailyItems, mergedPeriodicItems, orgId, stationId, deviceId, agentId]);

  const dailyData = useMemo(
    (): DailySummaryResponse => ({
      total: mergedDailyItems.length,
      days: 30,
      limit: 30,
      offset: 0,
      items: mergedDailyItems,
    }),
    [mergedDailyItems],
  );

  const periodicData = useMemo(
    (): PeriodicSummaryResponse => ({
      total: mergedPeriodicItems.length,
      limit: 30,
      offset: 0,
      items: mergedPeriodicItems,
    }),
    [mergedPeriodicItems],
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([
      stationLatest.refresh(),
      stationDaily.refresh(),
      stationPeriodic.refresh(),
      refreshEvents(),
    ]);
  }, [stationLatest, stationDaily, stationPeriodic, refreshEvents]);

  const loading = stationLatest.loading || stationDaily.loading || eventsLoading;
  const error = eventsError ?? stationLatest.error ?? stationDaily.error ?? stationPeriodic.error;

  return {
    latest: { data: latestData, loading, error, refresh: refreshAll },
    daily: { data: dailyData, loading, error, refresh: refreshAll },
    periodic: { data: periodicData, loading, error, refresh: refreshAll },
  };
}

function memoryMentionsDeviceSafe(summaryMd: string, deviceId: string, agentId?: string): boolean {
  const hay = summaryMd.toLowerCase();
  const needles = [deviceId, agentId]
    .map((s) => s?.trim())
    .filter((s): s is string => Boolean(s));
  return needles.some((n) => hay.includes(n.toLowerCase()));
}
